package com.gloopgo.game.network

import com.gloopgo.game.engine.EjectedMass
import com.gloopgo.game.engine.FoodPellet
import com.gloopgo.game.engine.GloopPlayer
import com.gloopgo.game.engine.LeaderboardEntry
import com.gloopgo.game.moderation.requireValidPlayerReport
import com.gloopgo.game.repository.LoggerRepository
import com.gloopgo.game.repository.LoggerRepositoryImpl
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.database.DataSnapshot
import dev.gitlive.firebase.database.database
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout
import kotlin.time.Duration.Companion.milliseconds

/**
 * Production Firebase implementation of MultiplayerRepository for Cross-Platform Multiplayer.
 *
 * All network timestamp keys (`ts`, `ja`) use primitive JSON Doubles so Kotlin/JS (Web)
 * and Android/JVM write identical flat numbers instead of nested `{ high, low }` JS objects.
 */
class FirebaseMultiplayerRepository(
    private val customRtdbUrl: String? = null,
    private val logger: LoggerRepository = LoggerRepositoryImpl()
) : MultiplayerRepository {

    companion object {
        private const val RTDB_URL = "https://gloopgo1-default-rtdb.firebaseio.com"
    }

    init {
        initPlatformFirebase()
        observeServerTimeOffset()
    }

    private val auth by lazy { Firebase.auth }
    private val rtdb by lazy { Firebase.database(customRtdbUrl ?: RTDB_URL) }
    private val firestore by lazy { Firebase.firestore }

    private var serverTimeOffset: Double = 0.0
    private var serverTimeOffsetJob: Job? = null

    private fun observeServerTimeOffset() {
        if (serverTimeOffsetJob != null) return
        serverTimeOffsetJob = CoroutineScope(Dispatchers.Default).launch {
            try {
                rtdb.reference(".info/serverTimeOffset").valueEvents.collect { snap ->
                    val offset = try {
                        snap.value<Double>()
                    } catch (_: Throwable) {
                        try {
                            snap.value<Long>().toDouble()
                        } catch (_: Throwable) {
                            0.0
                        }
                    }
                    serverTimeOffset = offset
                }
            } catch (_: Throwable) {
                // Keep local clock fallback (0.0 offset)
            }
        }
    }

    override suspend fun signInAnonymously(displayName: String): String {
        val existing = try { auth.currentUser } catch (e: Throwable) { null }
        if (existing != null && existing.uid.isNotEmpty()) {
            try {
                if (displayName.isNotEmpty()) {
                    existing.updateProfile(displayName = displayName)
                }
            } catch (e: Throwable) {}
            try {
                registerOrUpdateUserProfile(existing.uid, displayName, isAnonymous = true)
            } catch (e: Throwable) {}
            return existing.uid
        }

        val result = auth.signInAnonymously()
        val user = result.user ?: auth.currentUser
        val uid = user?.uid ?: throw IllegalStateException("Firebase returned null user after anonymous sign-in")

        try {
            if (displayName.isNotEmpty()) {
                user.updateProfile(displayName = displayName)
            }
        } catch (e: Throwable) {}

        try {
            registerOrUpdateUserProfile(uid, displayName, isAnonymous = true)
        } catch (e: Throwable) {}
        return uid
    }

    override suspend fun getCurrentUserId(): String? {
        return try {
            auth.currentUser?.uid
        } catch (e: Throwable) {
            null
        }
    }

    override suspend fun signOut() {
        try {
            try {
                withTimeout(5000L.milliseconds) {
                    auth.currentUser?.delete()
                }
            } catch (e: Throwable) {
                logger.d("auth.currentUser.delete note: ${e.message}, attempting auth.signOut()")
                try {
                    withTimeout(5000L.milliseconds) {
                        auth.signOut()
                    }
                } catch (_: Throwable) {
                }
            }
        } catch (e: Throwable) {
            logger.d("Sign out note: ${e.message}")
        }
    }

    override suspend fun clearUserData(userId: String): Result<Unit> {
        val targetUid = try {
            auth.currentUser?.uid?.takeIf { it.isNotBlank() }
        } catch (_: Throwable) {
            null
        } ?: userId.takeIf { it.isNotBlank() } ?: getCurrentUserId()?.takeIf { it.isNotBlank() }

        if (targetUid == null) {
            return Result.failure(IllegalStateException("No active user ID to clear"))
        }

        return try {
            val userRef = firestore.collection("users").document(targetUid)

            // Delete /users/{uid} while still authenticated so owner rules succeed.
            withTimeout(6000L.milliseconds) {
                userRef.delete()
            }
            logger.d("Deleted /users/$targetUid")

            suspend fun userDocExists(): Boolean {
                return try {
                    withTimeout(4000L.milliseconds) {
                        userRef.get().exists
                    }
                } catch (e: Throwable) {
                    logger.e("verify get /users/$targetUid failed: ${e.message}")
                    true
                }
            }

            if (userDocExists()) {
                logger.d("/users/$targetUid still present; retrying direct delete")
                withTimeout(6000L.milliseconds) {
                    userRef.delete()
                }
            }
            if (userDocExists()) {
                return Result.failure(
                    IllegalStateException("Failed to delete Firestore user document /users/$targetUid")
                )
            }
            logger.d("Verified /users/$targetUid is deleted")

            // Best-effort related cleanup — failures must not undo the user delete.
            try {
                withTimeout(3000L.milliseconds) {
                    firestore.collection("leaderboards").document(targetUid).delete()
                }
                logger.d("Deleted /leaderboards/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error deleting /leaderboards/$targetUid: ${e.message}")
            }

            try {
                withTimeout(4000L.milliseconds) {
                    val resultsRef = rtdb.reference("results").child(targetUid)
                    val snap = resultsRef.get()
                    for (child in snap.children) {
                        val key = child.key ?: continue
                        resultsRef.child(key).removeValue()
                    }
                }
                logger.d("Cleared results/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error clearing results/$targetUid: ${e.message}")
            }

            try {
                withTimeout(3000L.milliseconds) {
                    rtdb.reference("world/players").child(targetUid).removeValue()
                }
                logger.d("Deleted world/players/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error deleting world/players/$targetUid: ${e.message}")
            }

            try {
                withTimeout(4000L.milliseconds) {
                    val blocksRef = rtdb.reference("blocks").child(targetUid)
                    val snap = blocksRef.get()
                    for (child in snap.children) {
                        val key = child.key ?: continue
                        blocksRef.child(key).removeValue()
                    }
                }
                logger.d("Cleared blocks/$targetUid")
            } catch (e: Throwable) {
                logger.e("Error clearing blocks/$targetUid: ${e.message}")
            }

            delay(1000L.milliseconds)
            signOut()
            Result.success(Unit)
        } catch (e: Throwable) {
            logger.e("clearUserData error: ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun registerOrUpdateUserProfile(playerId: String, playerName: String, isAnonymous: Boolean) {
        try {
            val userRef = firestore.collection("users").document(playerId)
            val snapshot = userRef.get()
            val now = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
            val resolvedName = playerName.ifEmpty { "Player" }
            val profile = if (snapshot.exists) {
                val current = snapshot.data<UserProfile>()
                current.copy(
                    name = if (playerName.isNotEmpty()) playerName else current.name,
                    isAnonymous = isAnonymous
                )
            } else {
                UserProfile(
                    id = playerId,
                    name = resolvedName,
                    joinedAt = now,
                    isAnonymous = isAnonymous
                )
            }
            userRef.set(profile)
        } catch (e: Throwable) {
            logger.e("Error registering user profile: ${e.message}")
        }
    }

    override suspend fun recordGameJoined(playerId: String, playerName: String, isSinglePlayer: Boolean) {
        try {
            val userRef = firestore.collection("users").document(playerId)
            val snapshot = userRef.get()
            val now = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
            val resolvedName = playerName.ifEmpty { "Player" }
            val profile = if (snapshot.exists) {
                val current = snapshot.data<UserProfile>()
                if (isSinglePlayer) {
                    current.copy(
                        name = if (playerName.isNotEmpty()) playerName else current.name,
                        spGamesPlayed = current.spGamesPlayed + 1
                    )
                } else {
                    current.copy(
                        name = if (playerName.isNotEmpty()) playerName else current.name,
                        gamesPlayed = current.gamesPlayed + 1
                    )
                }
            } else {
                UserProfile(
                    id = playerId,
                    name = resolvedName,
                    joinedAt = now,
                    gamesPlayed = if (isSinglePlayer) 0 else 1,
                    spGamesPlayed = if (isSinglePlayer) 1 else 0,
                    isAnonymous = true
                )
            }
            userRef.set(profile)
        } catch (e: Throwable) {
            logger.e("Error recording game joined: ${e.message}")
        }
    }

    override fun observeUserProfile(playerId: String): Flow<UserProfile?> {
        if (playerId.isEmpty() || playerId == "current") {
            return flowOf(null)
        }
        return try {
            firestore.collection("users").document(playerId).snapshots
                .map { snapshot ->
                    if (snapshot.exists) snapshot.data<UserProfile>() else null
                }
                .catch { e ->
                    logger.e("observeUserProfile error: ${e.message}")
                    emit(null)
                }
        } catch (e: Throwable) {
            flowOf(null)
        }
    }

    override suspend fun recordGameStats(playerId: String, finalMass: Int, killedPlayer: Boolean, isSinglePlayer: Boolean) {
        try {
            val userRef = firestore.collection("users").document(playerId)
            val snapshot = userRef.get()
            if (snapshot.exists) {
                val current = snapshot.data<UserProfile>()
                val updated = if (isSinglePlayer) {
                    current.copy(
                        spBestMass = if (finalMass > 0) maxOf(current.spBestMass, finalMass) else current.spBestMass,
                        spTotalKills = current.spTotalKills + (if (killedPlayer) 1 else 0)
                    )
                } else {
                    current.copy(
                        bestMass = if (finalMass > 0) maxOf(current.bestMass, finalMass) else current.bestMass,
                        totalKills = current.totalKills + (if (killedPlayer) 1 else 0)
                    )
                }
                userRef.set(updated)
            }
        } catch (e: Throwable) {
            logger.e("Error recording game stats: ${e.message}")
        }
    }

    override suspend fun submitScore(playerName: String, score: Int, userId: String) {
        try {
            if (userId.isNotEmpty()) {
                val docRef = firestore.collection("leaderboards").document(userId)
                val snapshot = docRef.get()
                if (snapshot.exists) {
                    val existingScore = try { snapshot.get<Int>("score") } catch (e: Throwable) { 0 }
                    if (score > existingScore) {
                        docRef.set(
                            mapOf(
                                "name" to playerName,
                                "score" to score,
                                "userId" to userId,
                                "timestamp" to kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
                            )
                        )
                    }
                } else {
                    docRef.set(
                        mapOf(
                            "name" to playerName,
                            "score" to score,
                            "userId" to userId,
                            "timestamp" to kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
                        )
                    )
                }
            } else {
                logger.e("submitScore skipped: empty userId (leaderboard writes must be uid-scoped)")
            }
        } catch (e: Throwable) {
            logger.e("Error submitting score: ${e.message}")
        }
    }

    override fun observeLeaderboard(limit: Int): Flow<List<LeaderboardEntry>> {
        return try {
            firestore.collection("leaderboards")
                .snapshots
                .map { querySnapshot ->
                    val allEntries = querySnapshot.documents.mapNotNull { doc ->
                        val name = try { doc.get<String>("name") } catch (e: Throwable) { null }
                        val score = try { doc.get<Int>("score") } catch (e: Throwable) { null }
                        val uid = try { doc.get<String>("userId") } catch (e: Throwable) { null }
                        if (name != null && score != null) {
                            LeaderboardEntry(
                                id = uid ?: doc.id,
                                name = name,
                                score = score
                            )
                        } else null
                    }

                    allEntries
                        .groupBy { if (it.id.isNotEmpty()) it.id else it.name }
                        .map { (_, entries) -> entries.maxBy { it.score } }
                        .sortedByDescending { it.score }
                        .take(limit)
                }
                .catch { e ->
                    logger.e("observeLeaderboard error: ${e.message}")
                    emit(emptyList())
                }
        } catch (e: Throwable) {
            flowOf(emptyList())
        }
    }

    // Ephemeral World State (Realtime Database - Single Global Room)

    override suspend fun submitPlayerReport(
        reporterUid: String,
        reportedUid: String,
        reportedNickname: String,
        reason: String
    ): Result<Unit> {
        val signedInUid = getCurrentUserId()
        if (signedInUid.isNullOrBlank() || signedInUid != reporterUid) {
            return Result.failure(IllegalStateException("Reporter is not the signed-in user"))
        }
        return try {
            val nickname = requireValidPlayerReport(reporterUid, reportedUid, reportedNickname, reason)
            withTimeout(5000L.milliseconds) {
                rtdb.reference("reports").push().setValue(
                    PlayerReportRecord(
                        reporterUid = reporterUid,
                        reportedUid = reportedUid,
                        reportedNickname = nickname,
                        reason = reason
                    )
                ) {
                    encodeDefaults = true
                }
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            logger.e("submitPlayerReport error: ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun setPlayerBlocked(
        ownerUid: String,
        blockedUid: String,
        nickname: String,
        blocked: Boolean
    ): Result<Unit> {
        val signedInUid = getCurrentUserId()
        if (signedInUid.isNullOrBlank() || signedInUid != ownerUid) {
            return Result.failure(IllegalStateException("Block owner is not the signed-in user"))
        }
        if (blockedUid.isBlank() || blockedUid == ownerUid) {
            return Result.failure(IllegalArgumentException("Invalid block"))
        }
        return try {
            val ref = rtdb.reference("blocks").child(ownerUid).child(blockedUid)
            withTimeout(5000L.milliseconds) {
                if (blocked) {
                    val trimmed = nickname.trim()
                    if (trimmed.isEmpty() || trimmed.length > 32) {
                        throw IllegalArgumentException("Invalid nickname")
                    }
                    ref.setValue(PlayerBlockRecord(nickname = trimmed)) {
                        encodeDefaults = true
                    }
                } else {
                    ref.removeValue()
                }
            }
            Result.success(Unit)
        } catch (e: Throwable) {
            logger.e("setPlayerBlocked error: ${e.message}")
            Result.failure(e)
        }
    }

    override fun observeWorldPlayers(): Flow<List<PlayerNetworkState>> {
        return try {
            rtdb.reference("world/players").valueEvents.map { snapshot ->
                val list = mutableListOf<PlayerNetworkState>()
                for (child in snapshot.children) {
                    try {
                        val player = try {
                            child.value<PlayerNetworkState>()
                        } catch (e: Throwable) {
                            decodePlayerNetworkStateResilient(child)
                        }
                        if (player != null && player.id.isNotEmpty()) {
                            list.add(player)
                        }
                    } catch (e: Throwable) {
                        logger.e("observeWorldPlayers: error decoding child ${child.key}: ${e.message}")
                    }
                }
                list.toList()
            }.catch { e ->
                logger.e("observeWorldPlayers error: ${e.message}")
                emit(emptyList())
            }
        } catch (e: Throwable) {
            flowOf(emptyList())
        }
    }

    private fun decodePlayerNetworkStateResilient(child: DataSnapshot): PlayerNetworkState? {
        return try {
            val id = try { child.child("id").value<String>() } catch (_: Throwable) { child.key } ?: return null
            val name = try { child.child("n").value<String>() } catch (_: Throwable) { "Player" }
            val color = try {
                child.child("c").value<String>()
            } catch (_: Throwable) {
                try {
                    child.child("c").value<Long>().toString()
                } catch (_: Throwable) {
                    try {
                        child.child("c").value<Double>().toLong().toString()
                    } catch (_: Throwable) {
                        "4294967295"
                    }
                }
            }
            val emoji = try { child.child("e").value<String>() } catch (_: Throwable) { "" }
            val isAlive = try { child.child("a").value<Boolean>() } catch (_: Throwable) { true }
            val ts = try {
                child.child("ts").value<Double>()
            } catch (_: Throwable) {
                try {
                    child.child("ts").value<Long>().toDouble()
                } catch (_: Throwable) {
                    0.0
                }
            }
            val ja = try {
                child.child("ja").value<Double>()
            } catch (_: Throwable) {
                try {
                    child.child("ja").value<Long>().toDouble()
                } catch (_: Throwable) {
                    0.0
                }
            }

            val cells = mutableListOf<CellNetworkState>()
            val cellsNode = child.child("cells")
            for (cellChild in cellsNode.children) {
                try {
                    val cellId = try { cellChild.child("id").value<String>() } catch (_: Throwable) { cellChild.key } ?: "cell-0"
                    val x = try {
                        cellChild.child("x").value<Float>()
                    } catch (_: Throwable) {
                        try {
                            cellChild.child("x").value<Double>().toFloat()
                        } catch (_: Throwable) {
                            try {
                                cellChild.child("x").value<Long>().toFloat()
                            } catch (_: Throwable) {
                                0f
                            }
                        }
                    }
                    val y = try {
                        cellChild.child("y").value<Float>()
                    } catch (_: Throwable) {
                        try {
                            cellChild.child("y").value<Double>().toFloat()
                        } catch (_: Throwable) {
                            try {
                                cellChild.child("y").value<Long>().toFloat()
                            } catch (_: Throwable) {
                                0f
                            }
                        }
                    }
                    val mass = try {
                        cellChild.child("m").value<Float>()
                    } catch (_: Throwable) {
                        try {
                            cellChild.child("m").value<Double>().toFloat()
                        } catch (_: Throwable) {
                            try {
                                cellChild.child("m").value<Long>().toFloat()
                            } catch (_: Throwable) {
                                10f
                            }
                        }
                    }
                    cells.add(CellNetworkState(id = cellId, x = x, y = y, mass = mass))
                } catch (e: Throwable) {
                    logger.d("Skipping invalid cell in player $id: ${e.message}")
                }
            }

            PlayerNetworkState(
                id = id,
                name = name,
                color = color,
                emoji = emoji,
                cells = cells,
                isAlive = isAlive,
                lastTimestamp = ts,
                joinedAt = ja
            )
        } catch (e: Throwable) {
            logger.e("decodePlayerNetworkStateResilient failure for ${child.key}: ${e.message}")
            null
        }
    }

    override fun observeWorldPellets(): Flow<List<PelletNetworkState>> {
        return try {
            rtdb.reference("world/pellets").valueEvents.map { snapshot ->
                val list = mutableListOf<PelletNetworkState>()
                for (child in snapshot.children) {
                    try {
                        val pellet = try {
                            child.value<PelletNetworkState>()
                        } catch (_: Throwable) {
                            decodePelletNetworkStateResilient(child)
                        }
                        if (pellet != null && pellet.id.isNotEmpty()) {
                            list.add(pellet)
                        }
                    } catch (e: Throwable) {
                        logger.d("Skipping invalid pellet ${child.key}: ${e.message}")
                    }
                }
                list.toList()
            }.catch { e ->
                logger.e("observeWorldPellets error: ${e.message}")
                emit(emptyList())
            }
        } catch (e: Throwable) {
            flowOf(emptyList())
        }
    }

    private fun decodePelletNetworkStateResilient(child: DataSnapshot): PelletNetworkState? {
        return try {
            val id = try { child.child("id").value<String>() } catch (_: Throwable) { child.key } ?: return null
            val x = try {
                child.child("x").value<Float>()
            } catch (_: Throwable) {
                try { child.child("x").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val y = try {
                child.child("y").value<Float>()
            } catch (_: Throwable) {
                try { child.child("y").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val color = try {
                child.child("c").value<String>()
            } catch (_: Throwable) {
                try { child.child("c").value<Long>().toString() } catch (_: Throwable) { "4294967295" }
            }
            val mass = try {
                child.child("m").value<Float>()
            } catch (_: Throwable) {
                try { child.child("m").value<Double>().toFloat() } catch (_: Throwable) { 1f }
            }
            val emoji = try { child.child("e").value<String>() } catch (_: Throwable) { "" }
            PelletNetworkState(id = id, x = x, y = y, color = color, mass = mass, emoji = emoji)
        } catch (e: Throwable) {
            null
        }
    }

    override fun observeWorldEjected(): Flow<List<EjectedMassNetworkState>> {
        return try {
            rtdb.reference("world/ejected").valueEvents.map { snapshot ->
                val list = mutableListOf<EjectedMassNetworkState>()
                for (child in snapshot.children) {
                    try {
                        val mass = try {
                            child.value<EjectedMassNetworkState>()
                        } catch (_: Throwable) {
                            decodeEjectedMassNetworkStateResilient(child)
                        }
                        if (mass != null && mass.id.isNotEmpty()) {
                            list.add(mass)
                        }
                    } catch (e: Throwable) {
                        logger.d("Skipping invalid ejected mass ${child.key}: ${e.message}")
                    }
                }
                list.toList()
            }.catch { e ->
                logger.e("observeWorldEjected error: ${e.message}")
                emit(emptyList())
            }
        } catch (e: Throwable) {
            flowOf(emptyList())
        }
    }

    private fun decodeEjectedMassNetworkStateResilient(child: DataSnapshot): EjectedMassNetworkState? {
        return try {
            val id = try { child.child("id").value<String>() } catch (_: Throwable) { child.key } ?: return null
            val ownerId = try { child.child("o").value<String>() } catch (_: Throwable) { "" }
            val x = try {
                child.child("x").value<Float>()
            } catch (_: Throwable) {
                try { child.child("x").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val y = try {
                child.child("y").value<Float>()
            } catch (_: Throwable) {
                try { child.child("y").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val vx = try {
                child.child("vx").value<Float>()
            } catch (_: Throwable) {
                try { child.child("vx").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val vy = try {
                child.child("vy").value<Float>()
            } catch (_: Throwable) {
                try { child.child("vy").value<Double>().toFloat() } catch (_: Throwable) { 0f }
            }
            val color = try {
                child.child("c").value<String>()
            } catch (_: Throwable) {
                try { child.child("c").value<Long>().toString() } catch (_: Throwable) { "4294967295" }
            }
            val mass = try {
                child.child("m").value<Float>()
            } catch (_: Throwable) {
                try { child.child("m").value<Double>().toFloat() } catch (_: Throwable) { 12f }
            }
            val ts = try {
                child.child("ts").value<Double>()
            } catch (_: Throwable) {
                try { child.child("ts").value<Long>().toDouble() } catch (_: Throwable) { 0.0 }
            }
            EjectedMassNetworkState(id = id, ownerId = ownerId, x = x, y = y, vx = vx, vy = vy, color = color, mass = mass, createdAt = ts)
        } catch (e: Throwable) {
            null
        }
    }

    override suspend fun broadcastPlayerState(player: GloopPlayer, forceHeartbeat: Boolean) {
        try {
            val now = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble() + serverTimeOffset
            val networkState = PlayerNetworkState(
                id = player.id,
                name = player.name,
                color = player.color.toString(),
                emoji = player.emoji,
                cells = player.cells.map {
                    CellNetworkState(
                        id = it.id,
                        x = (it.position.x * 10).toInt() / 10f, // Rounded coordinates for compact payload
                        y = (it.position.y * 10).toInt() / 10f,
                        mass = (it.mass * 10).toInt() / 10f
                    )
                },
                isAlive = player.isAlive,
                lastTimestamp = now,
                joinedAt = player.joinedAt.toDouble()
            )
            rtdb.reference("world/players").child(player.id).setValue(networkState)
        } catch (e: Throwable) {
            logger.e("Error broadcasting player state: ${e.message}")
        }
    }

    override suspend fun spawnPellet(pellet: FoodPellet) {
        try {
            val dto = PelletNetworkState(
                id = pellet.id,
                x = (pellet.position.x * 10).toInt() / 10f,
                y = (pellet.position.y * 10).toInt() / 10f,
                color = pellet.color.toString(),
                mass = pellet.mass,
                emoji = pellet.emoji
            )
            rtdb.reference("world/pellets").child(pellet.id).setValue(dto)
        } catch (e: Throwable) {
            logger.e("Error spawning pellet: ${e.message}")
        }
    }

    override suspend fun spawnPellets(pellets: List<FoodPellet>) {
        if (pellets.isEmpty()) return
        try {
            val ref = rtdb.reference("world/pellets")
            for (pellet in pellets) {
                val dto = PelletNetworkState(
                    id = pellet.id,
                    x = (pellet.position.x * 10).toInt() / 10f,
                    y = (pellet.position.y * 10).toInt() / 10f,
                    color = pellet.color.toString(),
                    mass = pellet.mass,
                    emoji = pellet.emoji
                )
                ref.child(pellet.id).setValue(dto)
            }
        } catch (e: Throwable) {
            logger.e("Error batch spawning pellets: ${e.message}")
        }
    }

    override suspend fun removePellet(pelletId: String) {
        try {
            rtdb.reference("world/pellets").child(pelletId).removeValue()
        } catch (e: Throwable) {
            logger.e("Error removing pellet: ${e.message}")
        }
    }

    override suspend fun broadcastEjectedMass(mass: EjectedMass) {
        try {
            val dto = EjectedMassNetworkState(
                id = mass.id,
                ownerId = mass.ownerId,
                x = (mass.position.x * 10).toInt() / 10f,
                y = (mass.position.y * 10).toInt() / 10f,
                vx = (mass.velocity.x * 10).toInt() / 10f,
                vy = (mass.velocity.y * 10).toInt() / 10f,
                color = mass.color.toString(),
                mass = mass.mass,
                createdAt = if (mass.createdAt > 0L) mass.createdAt.toDouble() else kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
            )
            rtdb.reference("world/ejected").child(mass.id).setValue(dto)
        } catch (e: Throwable) {
            logger.e("Error broadcasting ejected mass: ${e.message}")
        }
    }

    override suspend fun removeEjectedMass(massId: String) {
        try {
            rtdb.reference("world/ejected").child(massId).removeValue()
        } catch (e: Throwable) {
            logger.e("Error removing ejected mass: ${e.message}")
        }
    }

    override suspend fun consumePlayerCell(
        eaterPlayerId: String,
        eaterCellId: String,
        victimPlayerId: String,
        victimCellId: String,
        isLastCell: Boolean
    ) {
        try {
            if (isLastCell) {
                rtdb.reference("world/players").child(victimPlayerId).removeValue()
                // Record kill result under victim results
                val sessionResult = SessionResult(
                    sessionId = "session-${kotlin.time.Clock.System.now().toEpochMilliseconds()}",
                    result = "lost_eaten",
                    timestamp = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble(),
                    finalMass = 0f,
                    killerName = eaterPlayerId
                )
                rtdb.reference("results").child(victimPlayerId).child(sessionResult.sessionId).setValue(sessionResult)
            } else {
                rtdb.reference("world/players").child(victimPlayerId).child("cells").child(victimCellId).removeValue()
            }
        } catch (e: Throwable) {
            logger.e("Error consuming player cell: ${e.message}")
        }
    }

    override suspend fun armDisconnectHandler(playerId: String, sessionId: String, approxMass: Float) {
        try {
            val playerNode = rtdb.reference("world/players").child(playerId)
            playerNode.onDisconnect().removeValue()

            val resultRef = rtdb.reference("results").child(playerId).child(sessionId)
            val sessionResult = SessionResult(
                sessionId = sessionId,
                result = "lost_disconnect",
                timestamp = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble(),
                finalMass = approxMass
            )
            resultRef.onDisconnect().setValue(sessionResult)
        } catch (e: Throwable) {
            // onDisconnect might not be available on all targets
        }
    }

    override suspend fun cancelDisconnectHandler(playerId: String) {
        try {
            rtdb.reference("world/players").child(playerId).onDisconnect().cancel()
        } catch (e: Throwable) {
            // Ignore
        }
    }

    override suspend fun pruneStalePlayer(stalePlayerId: String) {
        try {
            rtdb.reference("world/players").child(stalePlayerId).removeValue()
        } catch (e: Throwable) {
            logger.e("Error pruning stale player: ${e.message}")
        }
    }

    override suspend fun pruneStaleEjectedMasses(staleIds: List<String>) {
        for (id in staleIds) {
            removeEjectedMass(id)
        }
    }

    override suspend fun drainPendingSessionResults(playerId: String): List<SessionResult> {
        return try {
            emptyList()
        } catch (e: Throwable) {
            emptyList()
        }
    }

    override fun goOnline() {
        try {
            rtdb.goOnline()
        } catch (e: Throwable) {
            // Ignore
        }
    }

    override fun goOffline() {
        try {
            rtdb.goOffline()
        } catch (e: Throwable) {
            // Ignore
        }
    }

    override suspend fun leaveWorld(playerId: String) {
        try {
            cancelDisconnectHandler(playerId)
            try {
                withTimeout(2000L.milliseconds) {
                    rtdb.reference("world/players").child(playerId).removeValue()
                }
            } catch (e: Throwable) {
                logger.d("removeValue on leaveWorld timed out or note: ${e.message}")
            }
            delay(300L.milliseconds)
            goOffline()
        } catch (e: Throwable) {
            logger.e("Error leaving world: ${e.message}")
        }
    }
}
